// "Assign to identity" — the picker over the case's identities (SC-009).
// Pure: the rows, the query string and the refusal all arrive as props. The
// words that name what is assigned come from `copy`: faces by default, voices
// from the Unassigned voices tab (ADR-0077).
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { SearchInput } from '@/components/ui/search-input'
import { cn } from '@/utils/cn'

/** One row of the picker; the label is already composed by the caller. */
export interface AssignableIdentity {
  id: string
  label: string
}

interface AssignFacesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  faceCount: number
  identities: AssignableIdentity[]
  isLoading: boolean
  query: string
  onQueryChange: (query: string) => void
  selectedId: string | null
  onSelect: (identityId: string) => void
  onSubmit: () => void
  isSubmitting: boolean
  error: string | null
  /** The i18n base of the title, the description and the submit: what is being assigned. */
  copy?: string
}

export function AssignFacesDialog({
  open,
  onOpenChange,
  faceCount,
  identities,
  isLoading,
  query,
  onQueryChange,
  selectedId,
  onSelect,
  onSubmit,
  isSubmitting,
  error,
  copy = 'identity.unassigned.assign',
}: AssignFacesDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`${copy}.title`)}</DialogTitle>
          <DialogDescription>{t(`${copy}.description`, { count: faceCount })}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <SearchInput
            value={query}
            onValueChange={onQueryChange}
            placeholder={t('identity.searchPlaceholder')}
          />
          <ScrollArea className="h-64 rounded-md border border-border">
            <div className="p-1">
              {isLoading ? (
                <p className="p-4 text-sm text-muted-foreground">
                  {t('identity.unassigned.assign.loading')}
                </p>
              ) : identities.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">
                  {t('identity.unassigned.assign.noIdentities')}
                </p>
              ) : (
                identities.map(identity => (
                  <Button
                    key={identity.id}
                    type="button"
                    variant="ghost"
                    aria-pressed={selectedId === identity.id}
                    onClick={() => onSelect(identity.id)}
                    className={cn(
                      'w-full justify-start truncate',
                      selectedId === identity.id && 'bg-primary/10 text-foreground'
                    )}
                  >
                    {identity.label}
                  </Button>
                ))
              )}
            </div>
          </ScrollArea>
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting || !selectedId || faceCount === 0}>
            {t(`${copy}.submit`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
